import React, { useState } from 'react';
import { Users, Heart, Brain, DollarSign, ArrowUpRight, Zap, MessageCircle, Bell, Calendar, Clock, Sparkles, PieChart, UserCheck, Send, Filter } from 'lucide-react';
export function AudienceInsightsSection() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  // Sample data for audience insights
  const audienceSegments = [{
    id: 'curious',
    name: 'Curious',
    percentage: 32,
    color: 'bg-blue-500',
    growth: '+5%'
  }, {
    id: 'kinky',
    name: 'Kinky',
    percentage: 28,
    color: 'bg-purple-500',
    growth: '+12%'
  }, {
    id: 'lonely',
    name: 'Lonely',
    percentage: 18,
    color: 'bg-pink-500',
    growth: '-2%'
  }, {
    id: 'simpy',
    name: 'Simpy',
    percentage: 12,
    color: 'bg-green-500',
    growth: '+8%'
  }, {
    id: 'dominant',
    name: 'Dominant',
    percentage: 10,
    color: 'bg-orange-500',
    growth: '+3%'
  }];
  const topUsers = [{
    id: '1',
    name: 'CamWhale92',
    segment: 'curious',
    avatar: '🎮',
    avatarBg: 'bg-blue-500',
    lastActive: '2h ago',
    openRate: 92,
    clickRate: 78,
    spent: '$139.96',
    emotionalBond: 78,
    customRequests: 12,
    notes: 'High spender, prefers VIP content and custom toys',
    recommendedActions: ['Send a special toy-themed photo set', 'Offer exclusive VIP package with custom toy integration', 'Schedule a premium 1-on-1 live session']
  }, {
    id: '2',
    name: 'LonelyHeart84',
    segment: 'lonely',
    avatar: '❤️',
    avatarBg: 'bg-pink-500',
    lastActive: '30m ago',
    openRate: 98,
    clickRate: 65,
    spent: '$37.97',
    emotionalBond: 92,
    customRequests: 5,
    notes: 'Emotionally vulnerable, reacts to bonding & check-ins',
    recommendedActions: ['Send a personalized voice message', 'Check in with a "thinking of you" message', 'Offer emotional support subscription tier']
  }, {
    id: '3',
    name: 'DaddyDom69',
    segment: 'dominant',
    avatar: '🎯',
    avatarBg: 'bg-orange-500',
    lastActive: '1d ago',
    openRate: 75,
    clickRate: 62,
    spent: '$89.97',
    emotionalBond: 45,
    customRequests: 8,
    notes: 'Prefers dominant role, likes to give instructions',
    recommendedActions: ['Send submissive-themed content', 'Offer "follow instructions" custom video', 'Promote dominant roleplay package']
  }, {
    id: '4',
    name: 'KinkMaster',
    segment: 'kinky',
    avatar: '🔥',
    avatarBg: 'bg-purple-500',
    lastActive: '4h ago',
    openRate: 88,
    clickRate: 82,
    spent: '$112.50',
    emotionalBond: 65,
    customRequests: 15,
    notes: 'Into fetish content, likes experimental themes',
    recommendedActions: ['Share new fetish-themed photoshoot', 'Offer exclusive BDSM content package', 'Promote toy-integration session']
  }, {
    id: '5',
    name: 'SimpKing123',
    segment: 'simpy',
    avatar: '👑',
    avatarBg: 'bg-green-500',
    lastActive: '12h ago',
    openRate: 100,
    clickRate: 95,
    spent: '$245.75',
    emotionalBond: 85,
    customRequests: 20,
    notes: 'Top spender, buys everything, extremely devoted',
    recommendedActions: ['Offer exclusive "biggest fan" reward package', 'Send personalized thank you video', 'Create special VIP tier just for top fans']
  }];
  const preWrittenMessages = [{
    id: '1',
    title: 'Re-engage inactive users',
    message: "Hey! I've missed seeing you around lately. I just uploaded some new content I think you'd really enjoy. Come check it out? 💋",
    segment: 'all',
    conversionRate: '45%'
  }, {
    id: '2',
    title: 'Upsell to curious users',
    message: "I've been experimenting with some new toys and wanted to share the results with my VIP fans first... Want to see what happens next? 🎮💦",
    segment: 'curious',
    conversionRate: '62%'
  }, {
    id: '3',
    title: 'Connect with lonely users',
    message: "Just thinking about you today... I recorded a special voice message just for you. I'd love to hear how you've been doing too. 💕",
    segment: 'lonely',
    conversionRate: '78%'
  }, {
    id: '4',
    title: 'Challenge dominant users',
    message: "I've been a very good girl lately... but I'm wondering if you'd like to give me some instructions for my next private photo set? 😈",
    segment: 'dominant',
    conversionRate: '53%'
  }, {
    id: '5',
    title: 'Tease kinky users',
    message: 'I just got some new toys and outfits that I think would match your... interests perfectly. Want a sneak peek? 🔥🖤',
    segment: 'kinky',
    conversionRate: '67%'
  }];
  const getSelectedUser = () => {
    return topUsers.find(user => user.id === selectedUser) || null;
  };
  const filteredUsers = activeFilter === 'all' ? topUsers : topUsers.filter(user => user.segment === activeFilter);
  const filteredMessages = activeFilter === 'all' ? preWrittenMessages : preWrittenMessages.filter(msg => msg.segment === 'all' || msg.segment === activeFilter);
  return <div className="space-y-6" data-id="element-3547">
      {/* Emotional Segmentation Chart */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-3548">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" data-id="element-3549">
          <Users className="w-5 h-5 text-pink-500" data-id="element-3550" />
          Emotional Segmentation
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3551">
          {/* Pie Chart Visualization */}
          <div className="flex justify-center items-center" data-id="element-3552">
            <div className="relative w-48 h-48" data-id="element-3553">
              <PieChart className="w-full h-full text-gray-200 dark:text-gray-700" data-id="element-3554" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-3555">
                <div className="text-center" data-id="element-3556">
                  <span className="block text-2xl font-bold" data-id="element-3557">2.4k</span>
                  <span className="text-sm text-gray-500" data-id="element-3558">Total Fans</span>
                </div>
              </div>
            </div>
          </div>
          {/* Segment Breakdown */}
          <div className="space-y-3" data-id="element-3559">
            {audienceSegments.map(segment => <div key={segment.id} className="flex items-center" data-id="element-3560">
                <div className={`w-3 h-3 rounded-full ${segment.color} mr-2`} data-id="element-3561"></div>
                <div className="flex-1" data-id="element-3562">
                  <div className="flex justify-between items-center mb-1" data-id="element-3563">
                    <span className="text-sm font-medium" data-id="element-3564">{segment.name}</span>
                    <div className="flex items-center gap-1" data-id="element-3565">
                      <span className="text-sm" data-id="element-3566">{segment.percentage}%</span>
                      <span className={`text-xs ${segment.growth.startsWith('+') ? 'text-green-500' : 'text-red-500'}`} data-id="element-3567">
                        {segment.growth}
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full" data-id="element-3568">
                    <div className={`h-full ${segment.color} rounded-full`} style={{
                  width: `${segment.percentage}%`
                }} data-id="element-3569"></div>
                  </div>
                </div>
              </div>)}
            <button className="w-full mt-2 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors" data-id="element-3570">
              View Detailed Report
            </button>
          </div>
        </div>
      </div>
      {/* Top Active Users */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-3571">
        <div className="flex justify-between items-center mb-4" data-id="element-3572">
          <h3 className="text-lg font-semibold flex items-center gap-2" data-id="element-3573">
            <UserCheck className="w-5 h-5 text-blue-500" data-id="element-3574" />
            Top Active Users
          </h3>
          <div className="flex items-center gap-2" data-id="element-3575">
            <div className="relative" data-id="element-3576">
              <Filter className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-3577" />
              <select className="pl-8 pr-4 py-1 bg-gray-100 dark:bg-gray-700 rounded-md text-sm appearance-none" value={activeFilter} onChange={e => setActiveFilter(e.target.value)} data-id="element-3578">
                <option value="all" data-id="element-3579">All Segments</option>
                {audienceSegments.map(segment => <option key={segment.id} value={segment.id} data-id="element-3580">
                    {segment.name}
                  </option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-3581">
          <table className="w-full min-w-full" data-id="element-3582">
            <thead data-id="element-3583">
              <tr className="text-xs text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700" data-id="element-3584">
                <th className="pb-2 font-medium" data-id="element-3585">User</th>
                <th className="pb-2 font-medium" data-id="element-3586">Segment</th>
                <th className="pb-2 font-medium" data-id="element-3587">Open Rate</th>
                <th className="pb-2 font-medium" data-id="element-3588">Spent</th>
                <th className="pb-2 font-medium" data-id="element-3589">Bond</th>
                <th className="pb-2 font-medium" data-id="element-3590">Actions</th>
              </tr>
            </thead>
            <tbody data-id="element-3591">
              {filteredUsers.map(user => <tr key={user.id} className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50 ${selectedUser === user.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} data-id="element-3592">
                  <td className="py-3" data-id="element-3593">
                    <div className="flex items-center gap-2" data-id="element-3594">
                      <div className={`w-8 h-8 rounded-full ${user.avatarBg} flex items-center justify-center text-white`} data-id="element-3595">
                        <span data-id="element-3596">{user.avatar}</span>
                      </div>
                      <div data-id="element-3597">
                        <span className="font-medium text-sm block" data-id="element-3598">
                          {user.name}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-3599">
                          {user.lastActive}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="py-3" data-id="element-3600">
                    <span className="text-sm capitalize" data-id="element-3601">{user.segment}</span>
                  </td>
                  <td className="py-3" data-id="element-3602">
                    <div className="flex items-center gap-1" data-id="element-3603">
                      <span className="text-sm" data-id="element-3604">{user.openRate}%</span>
                      <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3605">
                        <div className="h-full bg-green-500 rounded-full" style={{
                      width: `${user.openRate}%`
                    }} data-id="element-3606"></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3" data-id="element-3607">
                    <span className="text-sm font-medium" data-id="element-3608">{user.spent}</span>
                  </td>
                  <td className="py-3" data-id="element-3609">
                    <div className="flex items-center gap-1" data-id="element-3610">
                      <Heart className={`w-4 h-4 ${user.emotionalBond > 80 ? 'text-pink-500' : 'text-gray-400'}`} fill={user.emotionalBond > 80 ? 'currentColor' : 'none'} data-id="element-3611" />
                      <span className="text-sm" data-id="element-3612">{user.emotionalBond}%</span>
                    </div>
                  </td>
                  <td className="py-3" data-id="element-3613">
                    <button className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium" onClick={() => setSelectedUser(selectedUser === user.id ? null : user.id)} data-id="element-3614">
                      {selectedUser === user.id ? 'Hide Details' : 'View Details'}
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Selected User Details */}
        {selectedUser && <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30" data-id="element-3615">
            <div className="flex justify-between items-start" data-id="element-3616">
              <div className="flex items-center gap-3" data-id="element-3617">
                <div className={`w-10 h-10 rounded-full ${getSelectedUser()?.avatarBg} flex items-center justify-center text-white`} data-id="element-3618">
                  <span data-id="element-3619">{getSelectedUser()?.avatar}</span>
                </div>
                <div data-id="element-3620">
                  <h4 className="font-medium" data-id="element-3621">{getSelectedUser()?.name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3622">
                    {getSelectedUser()?.notes}
                  </p>
                </div>
              </div>
              <div className="flex gap-2" data-id="element-3623">
                <button className="px-3 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-md text-xs font-medium" data-id="element-3624">
                  Message
                </button>
                <button className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-md text-xs font-medium" data-id="element-3625">
                  Create Campaign
                </button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3" data-id="element-3626">
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg" data-id="element-3627">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3628">
                  Open Rate
                </span>
                <span className="text-lg font-bold" data-id="element-3629">
                  {getSelectedUser()?.openRate}%
                </span>
              </div>
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg" data-id="element-3630">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3631">
                  Click Rate
                </span>
                <span className="text-lg font-bold" data-id="element-3632">
                  {getSelectedUser()?.clickRate}%
                </span>
              </div>
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg" data-id="element-3633">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3634">
                  Total Spent
                </span>
                <span className="text-lg font-bold" data-id="element-3635">
                  {getSelectedUser()?.spent}
                </span>
              </div>
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg" data-id="element-3636">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3637">
                  Custom Requests
                </span>
                <span className="text-lg font-bold" data-id="element-3638">
                  {getSelectedUser()?.customRequests}
                </span>
              </div>
            </div>
            <div className="mt-4" data-id="element-3639">
              <h5 className="font-medium text-sm mb-2" data-id="element-3640">Recommended Actions</h5>
              <ul className="space-y-2" data-id="element-3641">
                {getSelectedUser()?.recommendedActions.map((action, index) => <li key={index} className="flex items-start gap-2" data-id="element-3642">
                    <Zap className="w-4 h-4 text-yellow-500 mt-0.5" data-id="element-3643" />
                    <span className="text-sm" data-id="element-3644">{action}</span>
                  </li>)}
              </ul>
            </div>
          </div>}
      </div>
      {/* Recommended DM Messages */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-3645">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" data-id="element-3646">
          <MessageCircle className="w-5 h-5 text-purple-500" data-id="element-3647" />
          Recommended DM Messages
        </h3>
        <div className="space-y-3" data-id="element-3648">
          {filteredMessages.map(message => <div key={message.id} className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800" data-id="element-3649">
              <div className="flex justify-between items-start mb-2" data-id="element-3650">
                <h4 className="font-medium text-sm" data-id="element-3651">{message.title}</h4>
                <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full" data-id="element-3652">
                  {message.conversionRate} conversion
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-3653">
                "{message.message}"
              </p>
              <div className="flex justify-between items-center" data-id="element-3654">
                <span className="text-xs text-gray-500" data-id="element-3655">
                  Target:{' '}
                  <span className="capitalize" data-id="element-3656">
                    {message.segment === 'all' ? 'All Segments' : message.segment}
                  </span>
                </span>
                <div className="flex gap-2" data-id="element-3657">
                  <button className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-md text-xs" data-id="element-3658">
                    Edit
                  </button>
                  <button className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-md text-xs font-medium flex items-center gap-1" data-id="element-3659">
                    <Send className="w-3 h-3" data-id="element-3660" />
                    Send Now
                  </button>
                </div>
              </div>
            </div>)}
        </div>
        <div className="mt-4" data-id="element-3661">
          <button className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium" data-id="element-3662">
            Create New Message Template
          </button>
        </div>
      </div>
      {/* Auto-Messaging Builder */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-xl shadow-sm border border-purple-100 dark:border-purple-800/30" data-id="element-3663">
        <div className="flex items-center gap-2 mb-4" data-id="element-3664">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full" data-id="element-3665">
            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-3666" />
          </div>
          <h3 className="text-lg font-semibold" data-id="element-3667">
            Auto-Messaging Prompt Builder
          </h3>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg" data-id="element-3668">
          <div className="space-y-4" data-id="element-3669">
            <div data-id="element-3670">
              <label className="block text-sm font-medium mb-1" data-id="element-3671">
                Target Audience
              </label>
              <select className="w-full p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-sm" data-id="element-3672">
                <option data-id="element-3673">All users</option>
                <option data-id="element-3674">Curious users</option>
                <option data-id="element-3675">Kinky users</option>
                <option data-id="element-3676">Lonely users</option>
                <option data-id="element-3677">Simpy users</option>
                <option data-id="element-3678">Dominant users</option>
                <option data-id="element-3679">Inactive users (7+ days)</option>
                <option data-id="element-3680">High spenders ($50+)</option>
              </select>
            </div>
            <div data-id="element-3681">
              <label className="block text-sm font-medium mb-1" data-id="element-3682">
                Message Type
              </label>
              <select className="w-full p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-sm" data-id="element-3683">
                <option data-id="element-3684">Re-engagement message</option>
                <option data-id="element-3685">Upsell promotion</option>
                <option data-id="element-3686">New content notification</option>
                <option data-id="element-3687">Emotional connection</option>
                <option data-id="element-3688">Custom request invitation</option>
                <option data-id="element-3689">Special offer</option>
              </select>
            </div>
            <div data-id="element-3690">
              <label className="block text-sm font-medium mb-1" data-id="element-3691">
                Personality Mood
              </label>
              <div className="grid grid-cols-4 gap-2" data-id="element-3692">
                <button className="p-2 bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-md text-xs font-medium" data-id="element-3693">
                  Girlfriend
                </button>
                <button className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-md text-xs font-medium" data-id="element-3694">
                  Mistress
                </button>
                <button className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium" data-id="element-3695">
                  Innocent
                </button>
                <button className="p-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-md text-xs font-medium" data-id="element-3696">
                  Cosplay
                </button>
              </div>
            </div>
            <div data-id="element-3697">
              <label className="block text-sm font-medium mb-1" data-id="element-3698">
                Include Call-to-Action
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-3699">
                <button className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium" data-id="element-3700">
                  Buy Content
                </button>
                <button className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium" data-id="element-3701">
                  Schedule Call
                </button>
                <button className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium" data-id="element-3702">
                  Send Custom Request
                </button>
                <button className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium" data-id="element-3703">
                  Join Fan Club
                </button>
              </div>
            </div>
            <button className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium" data-id="element-3704">
              Generate Auto-Messages
            </button>
          </div>
        </div>
      </div>
    </div>;
}